import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, ArrowLeft, Mail, CheckCircle } from "lucide-react";
import { API } from "@/App";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [language, setLanguage] = useState("es");

  // Detect browser language
  useState(() => {
    const browserLang = navigator.language.toLowerCase();
    if (browserLang.includes("ca")) {
      setLanguage("ca");
    }
  }, []);

  const t = (es, ca) => language === "ca" ? ca : es;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email: email.toLowerCase(),
        language
      });
      setSent(true);
      toast.success(t(
        "Si existe una cuenta, recibirás un email",
        "Si existeix un compte, rebràs un email"
      ));
    } catch (error) {
      console.error("Forgot password error:", error);
      // Always show success to prevent email enumeration
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950 flex items-center justify-center px-4 py-12">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-96 h-96 bg-teal-400/10 rounded-full blur-3xl top-1/4 left-1/4 animate-pulse"></div>
          <div className="absolute w-96 h-96 bg-slate-400/10 rounded-full blur-3xl bottom-1/4 right-1/4 animate-pulse" style={{animationDelay: '1.5s'}}></div>
        </div>

        <div className="relative z-10 w-full max-w-md">
          <div className="glass rounded-3xl p-8 shadow-2xl border border-teal-400/20 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-green-600 mb-6">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-4">
              {t("¡Revisa tu correo!", "Revisa el teu correu!")}
            </h2>
            
            <p className="text-gray-400 mb-6">
              {t(
                "Si existe una cuenta asociada a este email, recibirás un enlace para restablecer tu contraseña.",
                "Si existeix un compte associat a aquest email, rebràs un enllaç per restablir la teva contrasenya."
              )}
            </p>

            <div className="bg-black/30 rounded-xl p-4 mb-6">
              <Mail className="w-6 h-6 text-teal-400 mx-auto mb-2" />
              <p className="text-sm text-gray-400">{email}</p>
            </div>

            <p className="text-sm text-gray-500 mb-6">
              {t(
                "El enlace expirará en 1 hora. Revisa también tu carpeta de spam.",
                "L'enllaç caducarà en 1 hora. Revisa també la carpeta de spam."
              )}
            </p>

            <Link 
              to="/auth"
              className="btn-primary inline-flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {t("Volver al inicio de sesión", "Tornar a l'inici de sessió")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950 flex items-center justify-center px-4 py-12">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-96 h-96 bg-teal-400/10 rounded-full blur-3xl top-1/4 left-1/4 animate-pulse"></div>
        <div className="absolute w-96 h-96 bg-slate-400/10 rounded-full blur-3xl bottom-1/4 right-1/4 animate-pulse" style={{animationDelay: '1.5s'}}></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="glass rounded-3xl p-8 shadow-2xl border border-teal-400/20">
          <Link 
            to="/auth" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-teal-300 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("Volver", "Tornar")}
          </Link>

          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">
              {t("¿Olvidaste tu contraseña?", "Has oblidat la contrasenya?")}
            </h2>
            <p className="text-gray-400">
              {t(
                "Te enviaremos un enlace para restablecerla",
                "T'enviarem un enllaç per restablir-la"
              )}
            </p>
          </div>

          <form data-testid="forgot-password-form" onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email
              </label>
              <input
                id="email"
                data-testid="email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-black/30 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/20 transition-all"
                placeholder="tu@email.com"
              />
            </div>

            <div>
              <label htmlFor="language" className="block text-sm font-medium text-gray-300 mb-2">
                {t("Idioma del email", "Idioma de l'email")}
              </label>
              <select
                id="language"
                data-testid="language-select"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full px-4 py-3 bg-black/30 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/20 transition-all"
              >
                <option value="es">Español</option>
                <option value="ca">Català</option>
              </select>
            </div>

            <button
              data-testid="submit-btn"
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t("Enviando...", "Enviant...") : t("Enviar enlace", "Enviar enllaç")}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/auth"
              data-testid="back-to-login"
              className="text-teal-300 hover:text-teal-200 transition-colors text-sm"
            >
              {t("Volver al inicio de sesión", "Tornar a l'inici de sessió")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
